import { useApi } from '@/api';
import type { GetSalesTransactionsResponse } from '@/api/handlers/resources/sales/responses/GetSalesTransactionsResponse';
import type { ApiResponse } from '@/api/handlers/responses/ApiResponse';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SalesQueryKeys } from './salesQueryKeys';

export type UseSalesTransactionsQueryParams = {
    from: string;
    pageSize?: number;
    sellerName?: string;
    to: string;
    transactionType?: 'Cash' | 'Cashless';
    discounted?: true;
};

/**
 * Paginated query for the Transactions widget. Backed by TanStack's
 * `useInfiniteQuery` so the widget can grow its row list as the user
 * scrolls. Each "page" the consumer reads is one `SalesTransactionsPage`
 * envelope from the server.
 *
 * `getNextPageParam` walks back through `allPages` to find the most
 * recent successful envelope and returns its `page + 1`. That keeps
 * `hasNextPage` truthy when a transient load-more failure lands on top
 * of a known-good page, so the UI can retry instead of being silently
 * pinned to the failed state.
 *
 * The `queryKey` includes the filter shape so any change to `from` /
 * `to` / `sellerName` / `transactionType` / `discounted` / `pageSize` resets the
 * page stack — switching the seller search or payment-type filter
 * re-fetches from page 1 automatically.
 */
export const useSalesTransactionsQuery = (
    params: UseSalesTransactionsQueryParams,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useInfiniteQuery<ApiResponse<GetSalesTransactionsResponse>>({
        queryKey: SalesQueryKeys.transactions(params),
        queryFn: ({ pageParam }) =>
            apiClient.sales.getSalesTransactions({
                from: params.from,
                pageSize: params.pageSize ?? 20,
                sellerName: params.sellerName,
                to: params.to,
                transactionType: params.transactionType,
                ...(params.discounted ? { discounted: params.discounted } : {}),
                page: pageParam as number,
            }),
        initialPageParam: 1,
        getNextPageParam: (_lastEnvelope, allEnvelopes) => {
            // Walk back to the latest successful page so a transient
            // failure on the last load doesn't permanently disable
            // load-more.
            for (let i = allEnvelopes.length - 1; i >= 0; i -= 1) {
                const envelope = allEnvelopes[i];
                if (envelope?.success) {
                    const { page, totalPages } = envelope.response;
                    return page < totalPages ? page + 1 : undefined;
                }
            }
            // No successful page yet — retry from page 1 of the next
            // attempt. (`undefined` would lock load-more out entirely.)
            return 1;
        },
        enabled,
        staleTime: 60_000,
    });
};
